import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import DashboardContent from "@/components/dashboard/DashboardContent";
import Settings from "@/pages/Settings";
import { globalProducts } from "@/data/productData";
import { globalSubscriptions } from "@/data/subscriptionData";
import { useStripeConnection } from "@/hooks/useStripeConnection";
import { useItemCounts } from "@/hooks/useItemCounts";
import ModalRoot from "@/components/modals/ModalRoot";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import StripePostConnectionFork from "@/components/stripe/StripePostConnectionFork";

const Dashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tabParam = searchParams.get("tab");
  const skipOnboarding = searchParams.get("skipOnboarding") === "true";

  const [activeTab, setActiveTab] = useState(tabParam || "savings_calculator");
  const [showStripeForkModal, setShowStripeForkModal] = useState(false);
  const { toast } = useToast();

  const {
    isConnected: stripeConnected,
    accountId: stripeAccountId,
    refreshConnection,
    disconnectError,
  } = useStripeConnection();
  
  const { fetchStripeCounts } = useItemCounts();
  
  // Store refreshConnection in a ref to avoid dependency issues
  const refreshConnectionRef = useRef(refreshConnection);
  useEffect(() => {
    refreshConnectionRef.current = refreshConnection;
  }, [refreshConnection]);

  // Use a ref to track if we've already refreshed to prevent multiple calls
  const hasRefreshedConnectionRef = useRef(false);
  
  useEffect(() => {
    if(disconnectError) {
      toast({ title: "Failed to disconnect from Stripe", variant: "destructive" });
    }

    if (!stripeConnected) {
      hasRefreshedConnectionRef.current = false;
    }
  }, [stripeConnected, disconnectError, toast]);
  
  const hasStripeAccount = !!stripeAccountId;

  const hasProducts =
    globalProducts.length > 0 || globalSubscriptions.length > 0;
  const hasData = hasProducts || hasStripeAccount;
  const hasCreatedFirstProduct =
    localStorage.getItem("hasCreatedFirstProduct") === "true";

  // ----------------------------
  // Handle Stripe refresh (once)
  // ----------------------------
  useEffect(() => {
    // Only refresh once on mount, not on every render or tab change
    if (!hasRefreshedConnectionRef.current) {
      refreshConnectionRef.current({ force: true });
      hasRefreshedConnectionRef.current = true;
    }
  }, []); 

  // ----------------------------
  // Handle tab param sync
  // ----------------------------
  // Sync activeTab with URL parameter only once on mount or when URL changes externally
  useEffect(() => {
    if (
      tabParam &&
      ["savings_calculator", "products", "bundles", "transactions", "settings", "subscriptions"].includes(tabParam)
    ) {
      setActiveTab(tabParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]); // tabParam intentionally excluded to prevent cycle
  // Update URL when activeTab changes (but only if different from current URL)
  useEffect(() => {
    if (tabParam !== activeTab) {
      navigate(`/dashboard?tab=${activeTab}`, { replace: true });
    }
  }, [activeTab, navigate]); // Remove tabParam from dependencies to prevent cycle

  // ----------------------------
  // Listen for Stripe connection event
  // ----------------------------
  useEffect(() => {
    const handleStripeConnected = () => {
      // Trigger transaction refresh via custom event (hook listens for this)
      document.dispatchEvent(new CustomEvent("transaction-force-refresh"));
    };

    document.addEventListener("stripe-connected", handleStripeConnected);
    return () => {
      document.removeEventListener("stripe-connected", handleStripeConnected);
    };
  }, []);

  useEffect(() => {
    const shouldShowFork = sessionStorage.getItem('showStripeForkModal');
    if (shouldShowFork === 'true') {
      setShowStripeForkModal(true);
      sessionStorage.removeItem('showStripeForkModal');
    }
  }, []);

  const handleForkOption = (option: 'migrate' | 'create' | 'skip') => {
    setShowStripeForkModal(false);
    
    switch (option) {
      case 'migrate':
        setActiveTab("subscriptions");
        navigate("/dashboard?tab=subscriptions&action=migrate", { replace: true });
        break;
      case 'create':
        setActiveTab("products");
        navigate("/dashboard?tab=products&action=create", { replace: true });
        break;
      case 'skip':
        setActiveTab("settings");
        navigate("/dashboard?tab=settings", { replace: true });
        break;
    }
  };

  return (
    <ModalRoot>
      <div className="min-h-screen bg-background">
        {/* Header is always visible */}
        <DashboardHeader activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Main content - no general blocking, specific components will handle their own blocking */}
        <main className="px-4 bg-background pt-2">
          {activeTab === "settings" ? (
            <Settings isSidebarCollapsed={false} />
          ) : (
            <DashboardContent
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              stripeConnected={stripeConnected}
              hasCreatedFirstProduct={hasCreatedFirstProduct}
              showSubscriptionsTab={true}
              isSidebarCollapsed={true}
            />
          )}
        </main>

        {/* Stripe Post-Connection Fork Modal */}
        <Dialog open={showStripeForkModal} onOpenChange={setShowStripeForkModal}>
          <DialogContent className="!fixed !left-1/2 !top-1/2 !-translate-x-1/2 !-translate-y-1/2 !right-auto w-[90%] sm:w-[80%] md:w-[65%] lg:w-[55%] xl:w-[45%] max-w-2xl !h-auto p-4 rounded-lg data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom">
            <StripePostConnectionFork onSelectOption={handleForkOption} />
          </DialogContent>
        </Dialog>
      </div>
    </ModalRoot>
  );
};

export default Dashboard;